export type AttendanceSyncRow = {
  name: string;
  work_date: string;
  area: string;
  regular_hours: number;
  overtime_hours: number;
  remark: string;
};

export type AttendanceSyncPayload = {
  action: "attendanceSave";
  project_id: string;
  attendance: AttendanceSyncRow[];
};

export type AttendanceQueueItem = {
  id: string;
  userId: string;
  createdAt: string;
  attempts: number;
  lastError: string;
  payload: AttendanceSyncPayload;
};

const STORAGE_KEY = "gongshiji-attendance-queue-v1";
const CHANGE_EVENT = "gongshiji-attendance-queue-change";

function readAll(): AttendanceQueueItem[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeAll(items: AttendanceQueueItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function queuedAttendance(userId: string) {
  return readAll().filter((item) => item.userId === userId);
}

export function queueAttendance(userId: string, payload: AttendanceSyncPayload) {
  const items = readAll();
  const signature = JSON.stringify(payload);
  const duplicate = items.find(
    (item) => item.userId === userId && JSON.stringify(item.payload) === signature,
  );
  if (duplicate) return duplicate;
  const item: AttendanceQueueItem = {
    id: crypto.randomUUID(),
    userId,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: "",
    payload,
  };
  writeAll([...items, item]);
  return item;
}

export function removeQueuedAttendance(id: string) {
  writeAll(readAll().filter((item) => item.id !== id));
}

export function updateQueuedAttendance(
  id: string,
  values: Pick<AttendanceQueueItem, "attempts" | "lastError">,
) {
  writeAll(
    readAll().map((item) => (item.id === id ? { ...item, ...values } : item)),
  );
}

export function clearQueuedAttendance(userId: string) {
  writeAll(readAll().filter((item) => item.userId !== userId));
}

export function onAttendanceQueueChange(listener: () => void) {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}

export class AttendanceSyncError extends Error {
  retryable: boolean;

  constructor(message: string, retryable: boolean) {
    super(message);
    this.name = "AttendanceSyncError";
    this.retryable = retryable;
  }
}

export async function sendAttendancePayload(
  payload: AttendanceSyncPayload,
  accessToken: string,
  signal?: AbortSignal,
) {
  let response: Response;
  try {
    response = await fetch("/api/sync", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${accessToken}`,
      },
      signal,
      body: JSON.stringify(payload),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new AttendanceSyncError(
      error instanceof Error ? error.message : "无法连接云端",
      true,
    );
  }
  const result = (await response.json().catch(() => ({}))) as {
    error?: string;
    attendance?: number;
  };
  if (!response.ok) {
    throw new AttendanceSyncError(
      result.error || `云端写入失败（${response.status}）`,
      response.status === 408 || response.status === 429 || response.status >= 500,
    );
  }
  return result;
}
